/**
 * @file ReviewView.tsx
 * @description
 * Displays the UI for a submission in IRB_REVIEW status. The user can see:
 *  1) A read-only version of their submission by clicking "View Locked Submission"
 *  2) Payment status (if submission.payment_completed === false, they see a "Complete Payment" button)
 *
 * Key Features:
 * - Payment link: If submission.payment_completed=false, show "Complete Payment" button that routes to /billing
 * - Read-Only Submission link: "View Locked Submission" to see the entire submission data in read-only mode
 *
 * @dependencies
 * - React, React Router (useNavigate) for navigation
 * - Payment gating is simplified (no real Stripe integration). We just link to a placeholder Billing page.
 *
 * @notes
 * - This finalizes Step 5 of the Implementation Plan for IRB_REVIEW status.
 * - In future steps, you might expand the Payment feature or show a partial or full summary directly on this page.
 */

import { DocumentDownloadLink } from '../DocumentDownloadLink';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Disclosure } from '@headlessui/react';
import { useSupabase } from '../../contexts/SupabaseContext';
import { Document } from '../../lib/supabaseClient';
import FeedbackAndResponses from '../FeedbackAndResponses';
import { SubmissionCapabilities, SubmissionRow } from '../../types/submission';

interface ReviewViewProps {
  capabilities: SubmissionCapabilities;
  submission: SubmissionRow;
}


/**
 * ReviewView
 * -----------
 * Renders the IRB_REVIEW stage UI with:
 * - A link to read-only submission form
 * - Payment status check
 * - Basic instructions
 */
const ReviewView: React.FC<ReviewViewProps> = ({ submission, capabilities }) => {
  const navigate = useNavigate();
  const [submissionFormPdfs, setSubmissionFormPdfs] = useState<Document[]>([]);

  // Create a memoized supabase client
  const supabase = useSupabase();

  console.log('Submission status:', {
    ready_for_pi_approval: submission.ready_for_pi_approval,
    type: typeof submission.ready_for_pi_approval,
    pi_approval: submission.pi_approval,
  });

  const handleViewLockedSubmission = () => {
    // We'll pass the submissionId in the query so the read-only page can load it
    navigate(`/view-submission?submissionId=${submission.id}`);
  };

  const handleCompletePayment = () => {
    // Send the user to the placeholder Billing page
    navigate(`/billing?submissionId=${submission.id}`);
  };

  // Fetch submission form PDFs
  useEffect(() => {
    const fetchSubmissionFormPdfs = async () => {
      try {
        const { data, error } = await supabase
          .from('documents')
          .select('*')
          .eq('submission_id', submission.id)
          .eq('type', 'submission_form_pdf')
          .eq('is_active', true);

        if (error) {
          console.error('Error fetching submission form PDFs:', error);
          return;
        }

        setSubmissionFormPdfs((data || []) as Document[]);
      } catch (error) {
        console.error('Failed to fetch submission form PDFs:', error);
      }
    };

    fetchSubmissionFormPdfs();
  }, [submission.id, supabase]);

  // Generate signed URLs for PDFs


  return (
    <div>
      <p className="text-gray-700 mb-6">
        Your submission is currently under IRB review. Learn more about Timely IRB's review process, and possible outcomes, <a href="https://timelyirb.com/review-process" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">here</a>.
      </p>

      {/* IRB Review Sub-Stages Section */}
      <div className="mb-6">
        <h3 className="font-semibold text-lg mb-3">IRB Review Progress</h3>

        {/* Stage 1: Initial IRB Review */}
        <div className="flex items-center mb-4">
          <div className="mr-3 w-7 h-7 flex-shrink-0">
            {Boolean(submission.ready_for_pi_approval) ? (
              <div className="w-full h-full rounded-full bg-green-500 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            ) : (
              <div className="w-full h-full rounded-full bg-blue-500 flex items-center justify-center">
                <span className="text-white text-xs font-bold">1</span>
              </div>
            )}
          </div>
          <div className="flex-grow">
            <h4 className="font-medium">1. Initial IRB Review for Submission Completeness</h4>
            <div className={`text-sm ${Boolean(submission.ready_for_pi_approval) ? 'text-green-600' : 'text-blue-600'}`}>
              {Boolean(submission.ready_for_pi_approval) ? 'Complete' : 'In Progress'}
            </div>
          </div>
        </div>

        {/* Stage 2: PI Approval */}
        <div className={`flex items-center mb-4 ${submission.ready_for_pi_approval ? '' : 'opacity-50'}`}>
          <div className="mr-3 w-7 h-7 flex-shrink-0">
            {submission.ready_for_pi_approval && submission.pi_approval ? (
              <div className="w-full h-full rounded-full bg-green-500 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            ) : (
              <div className={`w-full h-full rounded-full ${!submission.ready_for_pi_approval ? 'bg-gray-300' : 'bg-yellow-500'} flex items-center justify-center`}>
                <span className="text-white text-xs font-bold">2</span>
              </div>
            )}
          </div>
          <div className="flex-grow">
            <h4 className="font-medium">2. PI Approval of Complete Submission</h4>
            <div className={`text-sm ${!submission.ready_for_pi_approval ? 'text-gray-500' : submission.pi_approval ? 'text-green-600' : 'text-yellow-600'}`}>
              {!submission.ready_for_pi_approval ? 'Pending' : submission.pi_approval ? 'Complete' : 'In Progress - eSign request in PI email - remind them to sign!'}

              {submission.ready_for_pi_approval && !submission.pi_approval && submission.pi_approval_status_msg && (
                <div className="mt-1 text-xs italic text-gray-600">
                  {submission.pi_approval_status_msg}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stage 3: Final IRB Review */}
        <div className={`flex items-center ${submission.ready_for_pi_approval && submission.pi_approval ? '' : 'opacity-50'}`}>
          <div className="mr-3 w-7 h-7 flex-shrink-0">
            <div className={`w-full h-full rounded-full ${submission.ready_for_pi_approval && submission.pi_approval ? 'bg-blue-500' : 'bg-gray-300'} flex items-center justify-center`}>
              <span className="text-white text-xs font-bold">3</span>
            </div>
          </div>
          <div className="flex-grow">
            <h4 className="font-medium">3. Final IRB Review of Complete Submission</h4>
            <div className={`text-sm ${submission.ready_for_pi_approval && submission.pi_approval ? 'text-blue-600' : 'text-gray-500'}`}>
              {submission.ready_for_pi_approval && submission.pi_approval ? 'In Progress' : 'Pending'}
            </div>
          </div>
        </div>
      </div>

      {/* Payment Section */}
      <div className="mb-6">
        <h3 className="font-semibold text-lg mb-2">Payment Status</h3>
        {submission.payment_completed ? (
          <div className="p-3 bg-green-50 text-green-800 border border-green-200 rounded">
            Payment is complete!
          </div>
        ) : (
          <div className="p-3 text-[#B83EF5] border border-yellow-200 rounded flex flex-col space-y-3">
            <p>
              Payment has <strong>not</strong> been completed. IRB decisions will be gated until
              payment is received.
            </p>
            {capabilities.canCompletePayment ? (
              <button
                onClick={handleCompletePayment}
                className="px-4 py-2 bg-[#B83EF5] text-white rounded hover:bg-[#9c32c7]-50 self-start"
              >
                Complete Payment
              </button>
            ) : (
              <p className="text-sm text-gray-700">
                Payment is not available for this submission.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Locked Submission */}
      <div className="mb-6">
        <h3 className="font-semibold text-lg mb-2">View Submission (Read-Only)</h3>
        <p className="text-gray-700 mb-3">
          If you wish to review the submission details in a read-only format, click below:
        </p>
        <button
          onClick={handleViewLockedSubmission}
          className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700"
        >
          View Locked Submission
        </button>
      </div>

      <p className="text-gray-500 mt-8">
        If you have questions or concerns, please contact support@timelyirb.com.
      </p>

      {/* Data from All Review Cycles section */}
      {submission.review_data &&
        submission.review_data.review_cycles &&
        submission.review_data.review_cycles.length > 0 && (
          <Disclosure as="div" className="mt-8 mb-6">
            {({ open }) => (
              <>
                <Disclosure.Button className="flex justify-between w-full px-4 py-2 text-sm font-medium text-left text-blue-900 bg-blue-100 rounded-lg hover:bg-blue-200 focus:outline-none focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-opacity-75">
                  <span>IRB feedback & responses from all review cycles</span>
                  <svg
                    className={`w-5 h-5 text-blue-500 ${open ? 'transform rotate-180' : ''}`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </Disclosure.Button>
                <Disclosure.Panel className="px-4 pt-4 pb-2">
                  {submission.review_data?.review_cycles
                    // Show all cycles in reverse order (most recent first)
                    .slice().reverse()
                    .map((cycle, index) => (
                      <div key={`cycle-${index}`} className="mb-6 border-b pb-6 last:border-b-0">
                        <h4 className="font-semibold text-gray-700 mb-2">
                          Review Cycle {cycle.cycle}
                          {index === 0 && <span className="ml-2 text-blue-600 text-sm">(Current)</span>}
                        </h4>
                        <FeedbackAndResponses
                          submissionId={submission.id}
                          cycleNumber={cycle.cycle}
                          mode="view"
                        />
                      </div>
                    ))}
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        )}

      {/* Previous submission form PDFs section */}
      <Disclosure as="div" className="mt-4 mb-6">
        {({ open }) => (
          <>
            <Disclosure.Button className="flex justify-between w-full px-4 py-2 text-sm font-medium text-left text-blue-900 bg-blue-100 rounded-lg hover:bg-blue-200 focus:outline-none focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-opacity-75">
              <span>Submission form PDFs (previous and current)</span>
              <svg
                className={`w-5 h-5 text-blue-500 ${open ? 'transform rotate-180' : ''}`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </Disclosure.Button>
            <Disclosure.Panel className="px-4 pt-4 pb-2 text-sm text-gray-500">
              {submissionFormPdfs.length === 0 ? (
                <p className="text-gray-500 italic">No submission form PDFs found</p>
              ) : (
                <ul className="space-y-2">
                  {submissionFormPdfs.map((doc) => (
                    <li key={doc.id} className="flex items-center">
                      <svg
                        className="w-5 h-5 text-gray-600 mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                      </svg>
                      <DocumentDownloadLink document={doc} client={supabase} />
                      <span className="text-xs text-gray-500 ml-2">
                        (Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()})
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
    </div>
  );
};

export default ReviewView;
